/// Settings state, the web counterpart to the Riverpod handlers under
/// mobile/lib/settings/handler.
///
/// Zustand fills the same role as a Riverpod Notifier: one store per concern,
/// read through selector hooks, writes persisted immediately.

import { create } from 'zustand';

import type { SupportedLocale } from '@/app/i18n/resources';
import { colorsFor, type ThemeColors } from '@/app/theme/appColors';
import type { AppTheme } from '../data/settingsLocalData';
import {
  hasStoredTheme,
  readControlsSwapped,
  readDuckKey,
  readJumpKey,
  readLocale,
  readMusicEnabled,
  readMusicVolume,
  readSfxEnabled,
  readSfxVolume,
  readSystemTheme,
  readTheme,
  writeControlsSwapped,
  writeDuckKey,
  writeJumpKey,
  writeLocale,
  writeMusicEnabled,
  writeMusicVolume,
  writeSfxEnabled,
  writeSfxVolume,
  writeTheme,
} from '../data/settingsLocalData';
import {
  defaultDuckKey,
  defaultJumpKey,
  keysEqual,
  normalizeKey,
} from '../data/keyBindings';

interface SettingsState {
  theme: AppTheme;
  locale: SupportedLocale;
  musicEnabled: boolean;
  sfxEnabled: boolean;
  musicVolume: number;
  sfxVolume: number;
  controlsSwapped: boolean;
  jumpKey: string;
  duckKey: string;

  setTheme: (theme: AppTheme) => void;
  setLocale: (locale: SupportedLocale) => void;
  setMusicEnabled: (enabled: boolean) => void;
  setSfxEnabled: (enabled: boolean) => void;
  setMusicVolume: (volume: number) => void;
  setSfxVolume: (volume: number) => void;
  setControlsSwapped: (swapped: boolean) => void;
  setJumpKey: (key: string) => boolean;
  setDuckKey: (key: string) => boolean;
  resetKeyboardBindings: () => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  theme: readTheme(),
  locale: readLocale(),
  musicEnabled: readMusicEnabled(),
  sfxEnabled: readSfxEnabled(),
  musicVolume: readMusicVolume(),
  sfxVolume: readSfxVolume(),
  controlsSwapped: readControlsSwapped(),
  jumpKey: readJumpKey(),
  duckKey: readDuckKey(),

  setTheme: (theme) => {
    writeTheme(theme);
    set({ theme });
  },
  setLocale: (locale) => {
    writeLocale(locale);
    set({ locale });
  },
  setMusicEnabled: (musicEnabled) => {
    writeMusicEnabled(musicEnabled);
    set({ musicEnabled });
  },
  setSfxEnabled: (sfxEnabled) => {
    writeSfxEnabled(sfxEnabled);
    set({ sfxEnabled });
  },
  setMusicVolume: (musicVolume) => {
    writeMusicVolume(musicVolume);
    set({ musicVolume });
  },
  setSfxVolume: (sfxVolume) => {
    writeSfxVolume(sfxVolume);
    set({ sfxVolume });
  },
  setControlsSwapped: (controlsSwapped) => {
    writeControlsSwapped(controlsSwapped);
    set({ controlsSwapped });
  },
  setJumpKey: (key) => {
    const normalized = normalizeKey(key);
    const { duckKey } = useSettingsStore.getState();
    if (keysEqual(normalized, duckKey)) return false;
    writeJumpKey(normalized);
    set({ jumpKey: normalized });
    return true;
  },
  setDuckKey: (key) => {
    const normalized = normalizeKey(key);
    const { jumpKey } = useSettingsStore.getState();
    if (keysEqual(normalized, jumpKey)) return false;
    writeDuckKey(normalized);
    set({ duckKey: normalized });
    return true;
  },
  resetKeyboardBindings: () => {
    writeJumpKey(defaultJumpKey);
    writeDuckKey(defaultDuckKey);
    set({ jumpKey: defaultJumpKey, duckKey: defaultDuckKey });
  },
}));

/// Keeps theme in sync with the browser until the user picks Light/Dark in Settings.
export function startSystemThemeWatch(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const apply = (): void => {
    if (hasStoredTheme()) return;
    useSettingsStore.setState({ theme: readSystemTheme() });
  };

  apply();
  media.addEventListener('change', apply);
  return () => media.removeEventListener('change', apply);
}

export function useIsDarkMode(): boolean {
  return useSettingsStore((state) => state.theme === 'dark');
}

export function useThemeColors(): ThemeColors {
  return colorsFor(useIsDarkMode());
}
